import { API_ORIGIN, authorizedFetch, useAuth } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type JobCvImportRow = {
  id: string
  cv_document_id: string
  original_filename: string
  parsing_status: string
  source: string
  status: string
  application_id?: string | null
  error?: string
}

const CV_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export function jobCvAccept() {
  return CV_ACCEPT
}

function importsKey(opportunityId: string) {
  return ['job-cv-imports', opportunityId] as const
}

async function readError(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}))
  if (Array.isArray(body.details) && body.details.length > 0) {
    return body.details.join(' ')
  }
  return body.error || body.detail || fallback
}

export function useJobCvImports(opportunityId: string | undefined) {
  const { token } = useAuth()
  return useQuery({
    queryKey: opportunityId ? importsKey(opportunityId) : ['job-cv-imports', 'none'],
    enabled: Boolean(token && opportunityId),
    queryFn: async () => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/opportunities/${encodeURIComponent(opportunityId!)}/cv-imports/`,
      )
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to load CV imports'))
      }
      const data = (await response.json()) as { imports: JobCvImportRow[] }
      return data.imports
    },
    refetchInterval: (query) => {
      const rows = query.state.data
      if (!rows?.some((row) => row.status === 'pending' || row.parsing_status === 'pending' || row.parsing_status === 'processing')) {
        return false
      }
      return 2000
    },
  })
}

export function useUploadJobCvs(opportunityId: string) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (files: File[]) => {
      if (!token) throw new Error('Not authenticated')
      const formData = new FormData()
      for (const file of files) {
        formData.append('files', file)
      }
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/opportunities/${encodeURIComponent(opportunityId)}/cv-upload/`,
        { method: 'POST', body: formData },
      )
      if (!response.ok) {
        throw new Error(await readError(response, 'Failed to upload CVs'))
      }
      return (await response.json()) as { imports: JobCvImportRow[] }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: importsKey(opportunityId) })
      await queryClient.invalidateQueries({ queryKey: ['opportunity-applicants', opportunityId] })
    },
  })
}
